/* eslint-disable jsx-a11y/no-autofocus */
import type { MouseEvent } from 'react';
import { useRef, useState } from 'react';
import { breakpoints, Inline, vars } from '@etg/wings';
import { css, cx, mediaQueries, resetButtonStyle } from '@eti/styles';
import { useDirection, useSiteContext, useTranslation } from '@eti/providers';
import { Info } from '@phosphor-icons/react';
import { Icon } from '@eti/components';
import BaggageInformation from '../../drawer/baggage/BaggageInformation';
import { useOnClickOutside } from '../../../../../../common/custom-hooks/useOnClickOutside';
import type {
  AvailableExtraProductsType,
  BoundsType,
  IncludedCabinBaggageType,
  IncludedCheckedBaggageType,
} from '../../../../models';
import type {
  RebookingTripBoundsType,
  IncludedCabinBaggageType as RebookingTripIncludedCabinBaggageType,
} from '../../../../../change-trip/models';
import { hasLessThan2hSelfTransfer } from '../../utils/trip';
import { BaggageList } from './BaggageList';

const { signal } = vars.colors;

const cardStyles = css`
  background-color: ${signal.information.background};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  max-width: fit-content;
  padding: 8px;

  @media ${mediaQueries.small.only} {
    height: 40px;
  }
`;

const baggageInfoWrapperStyles = (isRTL: boolean) => css`
  background-color: ${signal.information.background};
  border-radius: ${vars.card.borderRadius};
  box-shadow:
    0 0 1px 0 rgba(0 0 0 / 4%),
    0 0 2px 0 rgba(0 0 0 / 6%),
    0 4px 8px 0 rgba(0 0 0 / 4%);
  padding: 2px;
  position: absolute;
  top: calc(100% + 8px);
  ${isRTL ? 'left: 0;' : 'right: 0;'}
  width: 322px;
  z-index: 1;
`;

const iconBaggageWrapperStyles = css`
  padding: 2px 0;
`;

const wrapperStyles = css`
  @media ${mediaQueries.small.only} {
    align-items: flex-end;
  }
`;

const closeButtonStyles = css`
  margin-right: 8px;
`;

const iconStyles = css`
  height: 0.7rem;
  user-select: none;
  width: 0.7rem;
`;

export type BaggageInfo = {
  availableExtraProducts: AvailableExtraProductsType;
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggageType | null;
};

interface TripBaggageInfoProps {
  availableExtraProducts?: AvailableExtraProductsType;
  bounds: BoundsType | RebookingTripBoundsType;
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggageType | null;
  isVI?: boolean;
  className?: string;
}

export const TripBaggageInfo = ({
  availableExtraProducts,
  bounds,
  includedCabinBaggage,
  includedCheckedBaggage,
  isVI = false,
  className = '',
}: TripBaggageInfoProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { language, timeFormat } = useSiteContext();
  const [shouldShowBaggagePopup, setShouldShowBaggagePopup] = useState(false);
  const isMobileDevice = window.matchMedia(`(max-width: ${breakpoints._768})`).matches;

  const baggageButtonRef = useRef<HTMLButtonElement | null>(null);
  const baggagePopUpRef = useRef<HTMLDivElement | null>(null);
  const baggageInfo = {
    availableExtraProducts,
    includedCabinBaggage,
    includedCheckedBaggage,
  } as BaggageInfo;
  const hasLessThan2hSelfTransferTrip = hasLessThan2hSelfTransfer(language, timeFormat, bounds);

  const handleBaggageClick = (event: MouseEvent<HTMLElement>) => {
    if (!isMobileDevice) {
      event.stopPropagation();
      setShouldShowBaggagePopup(true);
    }
  };

  const handleCloseBaggagePopUp = () => {
    baggageButtonRef.current?.focus();
    if (shouldShowBaggagePopup) {
      setShouldShowBaggagePopup(false);
    }
  };

  useOnClickOutside(baggagePopUpRef, handleCloseBaggagePopUp);

  return (
    <Inline align="end" className={wrapperStyles}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          className={cardStyles}
          data-testid="trip-baggage-info"
          onClick={handleBaggageClick}
          ref={baggageButtonRef}
          type="button"
        >
          <Inline className={iconBaggageWrapperStyles} spacing={8}>
            <Info
              aria-hidden
              color={vars.colors.signal.information.foreground}
              data-testid="baggage-information-icon"
              role="presentation"
              size="20"
              weight="fill"
            />
            <BaggageList baggageInfo={baggageInfo} />
          </Inline>
        </button>
        {shouldShowBaggagePopup && (
          <div className={cx(baggageInfoWrapperStyles(isRTL), className)} ref={baggagePopUpRef}>
            <Inline align="end" alignY="center">
              <button
                aria-label={t('ScreenReader.ResultCard.TripBaggageInfo.PopUpCloseButton.AriaLabel')}
                autoFocus
                className={cx(resetButtonStyle, closeButtonStyles)}
                data-testid="close-baggagepop-up-button"
                onClick={handleCloseBaggagePopUp}
                type="button"
              >
                <Icon className={iconStyles} id="close-menu" />
              </button>
            </Inline>
            <BaggageInformation
              baggageInfo={baggageInfo}
              hasLessThan2hSelfTransfer={hasLessThan2hSelfTransferTrip}
              isResultCardBaggageEnabled={shouldShowBaggagePopup}
              isVI={isVI}
            />
          </div>
        )}
      </div>
    </Inline>
  );
};
